import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { serial } from '../serial';
import { chart } from '../api/chart';
import { organisation, type Country, type LocationType } from '../api/organisation';
import { parameters, type ChecklistTitle } from '../api/parameters';
import { templates, type FormTemplateDetail, type VisitNotice } from '../api/templates';
import type { LibraryQuestion, QuestionCategory, ScoringModel, VisitType } from '../api/types';
import {
  IconArrowDown,
  IconArrowLeft,
  IconArrowRight,
  IconArchive,
  IconArrowUp,
  IconCheck,
  IconInbox,
  IconPlus,
  IconTrash2,
} from '../icons';
import { compare, fixed } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { distinctLabels } from '../labels';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { AnswerTypeBadge, BehaviourBadges } from '../ui/codes';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, Select, TextInput } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';

const percent = (share: number) => fixed(Math.round(share * 1000) / 10, 1);

/**
 * A row of toggles for one half of the scope.
 *
 * Chips rather than a multi-select box: the whole list is three or four items, and the
 * question "which of these is this form for" is answered faster by seeing all of them than
 * by opening something. Nothing chosen says so out loud, because nothing chosen means
 * everything — the one place where an empty control is a decision rather than a gap.
 */
function Picker({
  options,
  chosen,
  locked,
  allLabel,
  emptyLabel,
  onToggle,
}: {
  options: { id: string; label: string }[];
  chosen: string[];
  locked: boolean;
  allLabel: string;
  emptyLabel: string;
  onToggle: (ids: string[]) => void;
}) {
  if (options.length === 0) return <span className="nv-hint">{emptyLabel}</span>;

  return (
    <div className="nv-scope">
      {options.map((option) => {
        const on = chosen.includes(option.id);

        return (
          <button
            key={option.id}
            type="button"
            className="nv-scope__option"
            aria-pressed={on}
            disabled={locked}
            onClick={() =>
              onToggle(on ? chosen.filter((id) => id !== option.id) : [...chosen, option.id])
            }
          >
            {on ? <IconCheck size={13} /> : null}
            {option.label}
          </button>
        );
      })}
      {chosen.length === 0 ? <span className="nv-hint">{allLabel}</span> : null}
    </div>
  );
}

export function TemplateEditor() {
  const t = useTranslate();
  const navigate = useNavigate();
  const { id = '' } = useParams();

  const [detail, setDetail] = useState<FormTemplateDetail | null>(null);
  const [visitTypes, setVisitTypes] = useState<VisitType[]>([]);
  const [titles, setTitles] = useState<ChecklistTitle[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [locationTypes, setLocationTypes] = useState<LocationType[]>([]);
  const [categories, setCategories] = useState<QuestionCategory[]>([]);
  const [positions, setPositions] = useState<{ id: string; title: string; department: string }[]>([]);
  const [library, setLibrary] = useState<LibraryQuestion[]>([]);
  const [pickerCategory, setPickerCategory] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(true);

  /**
   * The category waiting to be confirmed, when it still holds questions.
   *
   * <p>An empty one goes on the first click, the way a question does. A full one takes its
   * questions with it — each can be picked out of the library again, but what they were
   * weighted at cannot, and that is the part nobody remembers.</p>
   */
  const [doomedCategory, setDoomedCategory] =
    useState<FormTemplateDetail['categories'][number] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, types, cats, titleList, countryList, typeList, seats] = await Promise.all([
        templates.get(id),
        api.visitTypes.list(),
        api.categories.list(),
        parameters.checklistTitles.list(),
        organisation.countries.list(),
        organisation.locationTypes.list(),
        chart.positions.list(),
      ]);
      setDetail(d);
      setVisitTypes(types);
      setCategories(cats);
      setTitles(titleList);
      setCountries(countryList);
      setLocationTypes(typeList);
      setPositions(seats.map((x) => ({ id: x.id, title: x.title, department: x.departmentName })));
      if (!pickerCategory && cats[0]) setPickerCategory(cats[0].id);
      setError(null);
    } catch (e) {
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
    // pickerCategory is read only to seed it once; re-running on every change would reset it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!pickerCategory) return;
    api.questions.list(pickerCategory).then(setLibrary).catch(() => setLibrary([]));
  }, [pickerCategory]);

  /**
   * Every mutation returns the whole template, so the shares are never stale — provided the
   * answers land in the order the edits were made. Typed quickly, weights blur one after
   * another and their answers raced: an older whole template overwrote a newer one and the
   * total read 53 where the server held 100.5 (field test B-42). One at a time, the last
   * answer is always the newest.
   */
  const oneAtATime = useRef(serial()).current;

  function act(run: () => Promise<FormTemplateDetail>, fallback = 'error.actionFailed'): Promise<boolean> {
    return oneAtATime(async () => {
      try {
        setDetail(await run());
        setError(null);
        return true;
      } catch (e) {
        setError(describeError(e, fallback));
        return false;
      }
    });
  }

  /**
   * A weight the server refused goes back to what it holds. The input keeps its own value,
   * so without this a refused 0 stayed on screen looking saved (field test B-41).
   */
  async function saveWeight(input: HTMLInputElement, held: number, run: () => Promise<FormTemplateDetail>) {
    if (Number(input.value) === held) return;
    if (!(await act(run))) input.value = String(held);
  }

  if (loading && !detail) return <p className="nv-hint">{t('common.loading')}</p>;

  if (!detail) {
    return (
      <EmptyState
        icon={IconInbox}
        title={t('templates.notFound')}
        action={
          <Button icon={IconArrowLeft} onClick={() => navigate('/sablonlar')}>
            {t('templates.backToList')}
          </Button>
        }
      />
    );
  }

  const s = detail.summary;
  const locked = s.status !== 'Draft';
  // By link and by code, matching the server: a question on the form through either is
  // one the server will skip, and a tick box that does nothing is worse than a greyed one.
  const formQuestions = detail.categories.flatMap((c) => c.questions);
  const onForm = new Set(formQuestions.map((q) => q.sourceQuestionId));
  const codesOnForm = new Set(formQuestions.map((q) => q.code).filter(Boolean));
  const locationTypeLabels = distinctLabels(locationTypes);
  const points = s.isScored && s.scoringModel === 'Points';

  // One label for both models, chosen once. The column means the same thing either way —
  // what this question is worth — and only the word for it changes.
  const weightLabel = points ? 'templates.points' : 'templates.weight';

  const totalWeight = s.totalQuestionWeight;

  // Under points the total has a target and missing it stops publication. Compared here
  // only to colour the chip; the refusal itself lives in the domain, where both doors
  // through to publishing pass.
  const offTarget = points && s.pointsTarget !== null && totalWeight !== s.pointsTarget;

  function save(change: Partial<Parameters<typeof templates.update>[1]>) {
    return act(() =>
      templates.update(id, {
        code: s.code,
        name: s.name,
        description: s.description,
        visitTypeId: s.visitTypeId,
        checklistTitleId: s.checklistTitleId,
        countryIds: s.countryIds,
        locationTypeIds: s.locationTypeIds,
        requiresSignature: s.requiresSignature,
        notice: s.notice,
        isScored: s.isScored,
        effectiveFrom: s.effectiveFrom,
        effectiveTo: s.effectiveTo,
        ...change,
      }), 'error.saveFailed');
  }

  return (
    <>
      <PageHeader
        crumbs={[t('app.templates'), s.code]}
        title={s.name}
        subtitle={t('templates.summary', {
          questions: s.questionCount,
          categories: detail.categories.length,
        })}
        aside={
          <Badge tone={s.status === 'Published' ? 'comment' : s.status === 'Archived' ? 'na' : 'neutral'}>
            {`${t(`formStatus.${s.status}`)} · v${s.version}`}
          </Badge>
        }
        action={
          <Button icon={IconArrowLeft} onClick={() => navigate('/sablonlar')}>
            {t('templates.backToList')}
          </Button>
        }
      />

      <div className="nv-stack">
        {error ? <Alert>{error}</Alert> : null}

        {/* ---------------------------------------------------------------- details */}
        <Card className="nv-pad">
          <div className="nv-form-grid">
            <Field label={t('templates.name')}>
              {({ id: fieldId }) => (
                <TextInput
                  id={fieldId}
                  disabled={locked}
                  defaultValue={s.name}
                  onBlur={(e) => e.target.value !== s.name && void save({ name: e.target.value })}
                />
              )}
            </Field>

            <Field label={t('templates.visitType')}>
              {({ id: fieldId }) => (
                <Select
                  id={fieldId}
                  disabled={locked}
                  value={s.visitTypeId ?? ''}
                  onChange={(e) => void save({ visitTypeId: e.target.value || null })}
                >
                  <option value="">{t('common.none')}</option>
                  {visitTypes.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label={t('templates.checklistTitle')}>
              {({ id: fieldId }) => (
                <Select
                  id={fieldId}
                  disabled={locked}
                  value={s.checklistTitleId ?? ''}
                  onChange={(e) => void save({ checklistTitleId: e.target.value || null })}
                >
                  <option value="">{t('common.none')}</option>
                  {titles.map((x) => (
                    <option key={x.id} value={x.id}>{x.name}</option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label={t('templates.scoring')}>
              {({ id: fieldId }) => (
                <Select
                  id={fieldId}
                  disabled={locked}
                  value={s.isScored ? 'y' : 'n'}
                  onChange={(e) => void save({ isScored: e.target.value === 'y' })}
                >
                  <option value="y">{t('templates.scored')}</option>
                  <option value="n">{t('templates.unscored')}</option>
                </Select>
              )}
            </Field>

            {/*
              * Only on a scored form. On an unscored one there is no model to choose
              * between, and an option that governs nothing invites somebody to set it and
              * wonder later why it made no difference.
              */}
            {s.isScored ? (
              <Field label={t('templates.scoringModel')} hint={t('templates.scoringModelHint')}>
                {({ id: fieldId, describedBy }) => (
                  <Select
                    id={fieldId}
                    aria-describedby={describedBy}
                    disabled={locked}
                    value={s.scoringModel}
                    onChange={(e) =>
                      void act(() =>
                        templates.setScoringModel(id, {
                          model: e.target.value as ScoringModel,
                          pointsTarget: s.pointsTarget,
                        }),
                      )
                    }
                  >
                    <option value="Weight">{t('scoringModel.Weight')}</option>
                    <option value="Points">{t('scoringModel.Points')}</option>
                  </Select>
                )}
              </Field>
            ) : null}

            <Field label={t('templates.signature')}>
              {({ id: fieldId }) => (
                <Select
                  id={fieldId}
                  disabled={locked}
                  value={s.requiresSignature ? 'y' : 'n'}
                  onChange={(e) => void save({ requiresSignature: e.target.value === 'y' })}
                >
                  <option value="n">{t('templates.signatureNo')}</option>
                  <option value="y">{t('templates.signatureYes')}</option>
                </Select>
              )}
            </Field>

            <Field label={t('templates.notice')}>
              {({ id: fieldId }) => (
                <Select
                  id={fieldId}
                  disabled={locked}
                  value={s.notice}
                  onChange={(e) => void save({ notice: e.target.value as VisitNotice })}
                >
                  <option value="Announced">{t('notice.Announced')}</option>
                  <option value="Unannounced">{t('notice.Unannounced')}</option>
                </Select>
              )}
            </Field>

            <Field label={t('templates.from')}>
              {({ id: fieldId }) => (
                <TextInput
                  id={fieldId}
                  type="date"
                  disabled={locked}
                  defaultValue={s.effectiveFrom ?? ''}
                  onBlur={(e) => void save({ effectiveFrom: e.target.value || null })}
                />
              )}
            </Field>

            <Field label={t('templates.to')}>
              {({ id: fieldId }) => (
                <TextInput
                  id={fieldId}
                  type="date"
                  disabled={locked}
                  defaultValue={s.effectiveTo ?? ''}
                  onBlur={(e) => void save({ effectiveTo: e.target.value || null })}
                />
              )}
            </Field>
          </div>

          {/*
            Where this checklist applies. Empty is not "unset" — it is the answer: every
            market, every kind of shop. Narrowing is something a designer does on purpose,
            and until they do, the form is offered everywhere.
          */}
          <div className="nv-drawer__section">
            <h3 className="nv-h3">{t('templates.scope')}</h3>
            <p className="nv-hint">{t('templates.scopeHint')}</p>

            <div className="nv-form-pair">
            <div className="nv-field">
              <span className="nv-label">{t('templates.countries')}</span>
              <Picker
                options={countries.map((c) => ({ id: c.id, label: c.name }))}
                chosen={s.countryIds}
                locked={locked}
                allLabel={t('templates.everyCountry')}
                emptyLabel={t('templates.noCountries')}
                onToggle={(ids) => void save({ countryIds: ids })}
              />
            </div>

            <div className="nv-field">
              <span className="nv-label">{t('templates.locationTypes')}</span>
              <Picker
                // Two types really are called AVM here. The code is unique, the name is not,
                // and two identical chips are a guess rather than a choice.
                options={locationTypes.map((x) => ({
                  id: x.id,
                  label: locationTypeLabels.get(x.id) ?? x.name,
                }))}
                chosen={s.locationTypeIds}
                locked={locked}
                allLabel={t('templates.everyLocationType')}
                emptyLabel={t('templates.noLocationTypes')}
                onToggle={(ids) => void save({ locationTypeIds: ids })}
              />
            </div>
            </div>
          </div>

          <div className="nv-drawer__section">
            <h3 className="nv-h3">{t('templates.approvalChain')}</h3>
            <p className="nv-hint">{t('templates.approvalChainHint')}</p>

            <Field label={t('templates.addApprover')}>
              {({ id: fieldId, describedBy }) => (
                <Select
                  id={fieldId}
                  aria-describedby={describedBy}
                  disabled={locked}
                  value=""
                  onChange={(e) => {
                    if (!e.target.value) return;

                    // Appended rather than inserted: the order is the order approvals
                    // happen in, and somebody building it is building it top down.
                    void act(() =>
                      templates.setApprovalSteps(id, [...s.approvalSteps, e.target.value]),
                    );
                  }}
                >
                  <option value="">{t('templates.addApprover')}</option>
                  {/* By department: fifty-four seats in one flat list was a scroll, not a choice
                      (field test, 2 November). */}
                  {[...new Set(positions.map((x) => x.department))].sort(compare).map((department) => (
                    <optgroup key={department} label={department}>
                      {positions
                        .filter((x) => x.department === department && !s.approvalSteps.includes(x.id))
                        .map((x) => (
                          <option key={x.id} value={x.id}>{x.title}</option>
                        ))}
                    </optgroup>
                  ))}
                </Select>
              )}
            </Field>

            {s.approvalSteps.length > 0 ? (
              <div className="nv-field">
                <span className="nv-label">{t('templates.chainOrder')}</span>
                <ol className="nv-reasons">
                  {s.approvalStepTitles.map((title, i) => (
                    <li key={s.approvalSteps[i]}>
                      {title}
                      {locked ? null : (
                        <IconButton
                          label={t('templates.removeApprover')}
                          icon={IconTrash2}
                          tone="danger"
                          onClick={() =>
                            void act(() =>
                              templates.setApprovalSteps(
                                id,
                                s.approvalSteps.filter((_, n) => n !== i),
                              ),
                            )
                          }
                        />
                      )}
                    </li>
                  ))}
                </ol>
                <p className="nv-hint">{t('templates.chainOrderHint')}</p>
              </div>
            ) : (
              <p className="nv-hint">{t('templates.noChain')}</p>
            )}
          </div>
        </Card>

        {/* ---------------------------------------------------------------- questions + picker */}
        <div className="nv-split">
          <Card className="nv-table-card">
            <table className="nv-table">
              <caption className="nv-sr-only">{t('templates.questions')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('questions.code')}</th>
                  <th scope="col">{t('questions.text')}</th>
                  <th scope="col">{t('questions.type')}</th>
                  <th scope="col" className="is-numeric">{t(weightLabel)}</th>
                  <th scope="col" className="is-numeric">{t('templates.share')}</th>
                  <th scope="col" className="nv-actions-col">
                    <span className="nv-sr-only">{t('common.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.categories.map((category) => (
                  <Fragment key={category.id}>
                    <tr className="is-group">
                      <td colSpan={3}>
                        <span className="nv-badge-group">
                          <strong>{category.name}</strong>
                          <Badge tone="neutral" shape="square">
                            {t('categories.questionCount', { count: category.questions.length })}
                          </Badge>
                        </span>
                      </td>
                      {/*
                        * Under points this is the sum of the questions below it, so it is
                        * shown and not offered. An input here would be a second number
                        * that can disagree with the first, and the screen would be showing
                        * the one that is wrong.
                        */}
                      <td className="is-numeric">
                        {locked || points ? (
                          category.weight
                        ) : (
                          <input
                            type="number"
                            min={0.01}
                            step={1}
                            className="nv-input nv-weight"
                            aria-label={`${category.name} · ${t(weightLabel)}`}
                            defaultValue={category.weight}
                            onBlur={(e) =>
                              void saveWeight(e.target, category.weight, () =>
                                templates.setCategoryWeight(id, category.id, Number(e.target.value)),
                              )
                            }
                          />
                        )}
                      </td>
                      <td className="is-numeric nv-code">%{percent(category.share)}</td>
                      <td className="nv-actions-col">
                        {locked ? null : (
                          <IconButton
                            label={t('templates.removeCategory')}
                            icon={IconTrash2}
                            tone="danger"
                            onClick={() =>
                              category.questions.length === 0
                                ? void act(() => templates.removeCategory(id, category.id))
                                : setDoomedCategory(category)
                            }
                          />
                        )}
                      </td>
                    </tr>

                    {category.questions.map((question, index) => (
                      <tr key={question.id}>
                        <td data-label={t('questions.code')} className="nv-code">
                          {question.code}
                        </td>
                        <td data-label={t('questions.text')} className="is-primary">
                          {question.text}
                          <BehaviourBadges question={question} max={2} />
                        </td>
                        <td data-label={t('questions.type')}>
                          <span className="nv-badge-group">
                            <AnswerTypeBadge type={question.answerType} />
                            {question.answerType === 'Scale' ? (
                              <span className="nv-subtle">1–{question.scaleMaximum}</span>
                            ) : null}
                          </span>
                        </td>
                        <td data-label={t(weightLabel)} className="is-numeric">
                          {locked ? (
                            question.weight
                          ) : (
                            <input
                              type="number"
                              min={0.01}
                              step={1}
                              className="nv-input nv-weight"
                              aria-label={`${question.code} · ${t(weightLabel)}`}
                              defaultValue={question.weight}
                              onBlur={(e) =>
                                void saveWeight(e.target, question.weight, () =>
                                  templates.setQuestionWeight(id, question.id, Number(e.target.value)),
                                )
                              }
                            />
                          )}
                        </td>
                        <td data-label={t('templates.share')} className="is-numeric nv-code">
                          %{percent(question.share)}
                        </td>
                        <td className="nv-actions-col">
                          <div className="nv-row-actions">
                            <IconButton
                              label={t('templates.moveUp')}
                              icon={IconArrowUp}
                              disabled={locked || index === 0}
                              onClick={() => void act(() => templates.moveQuestion(id, question.id, index))}
                            />
                            <IconButton
                              label={t('templates.moveDown')}
                              icon={IconArrowDown}
                              disabled={locked || index === category.questions.length - 1}
                              onClick={() => void act(() => templates.moveQuestion(id, question.id, index + 2))}
                            />
                            <IconButton
                              label={t('templates.removeQuestion')}
                              icon={IconTrash2}
                              tone="danger"
                              disabled={locked}
                              onClick={() => void act(() => templates.removeQuestion(id, question.id))}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>

            {detail.categories.length === 0 ? (
              <EmptyState icon={IconInbox} title={t('templates.noQuestions')} text={t('templates.noQuestionsText')} />
            ) : null}
          </Card>

          {/* ---------------------------------------------------------------- the picker */}
          <Card className="nv-picker">
            <div className="nv-picker__head">
              <h3 className="nv-h3">{t('templates.addQuestions')}</h3>
              <Select
                aria-label={t('questions.category')}
                disabled={locked}
                value={pickerCategory}
                onChange={(e) => {
                  setPickerCategory(e.target.value);
                  setPicked(new Set());
                }}
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{`${c.name} (${c.questionCount})`}</option>
                ))}
              </Select>
            </div>

            <div className="nv-picker__body">
              {library.map((q) => {
                const already = onForm.has(q.id) || codesOnForm.has(q.code);

                return (
                  <label key={q.id} className={already ? 'nv-picker__row is-taken' : 'nv-picker__row'}>
                    <input
                      type="checkbox"
                      disabled={already || locked}
                      checked={picked.has(q.id)}
                      onChange={(e) => {
                        const next = new Set(picked);
                        if (e.target.checked) next.add(q.id);
                        else next.delete(q.id);
                        setPicked(next);
                      }}
                    />
                    <span>
                      <span className="nv-code">{q.code}</span> {q.text}
                      {already ? <span className="nv-hint">{t('templates.alreadyOnForm')}</span> : null}
                    </span>
                  </label>
                );
              })}

              {library.length === 0 ? (
                <EmptyState icon={IconInbox} title={t('questions.empty')} />
              ) : null}
            </div>

            <div className="nv-picker__foot">
              <span className="nv-hint">
                {picked.size > 0 ? t('templates.picked', { count: picked.size }) : t('templates.pickSome')}
              </span>
              <Button
                variant="primary"
                size="sm"
                icon={IconPlus}
                disabled={picked.size === 0 || locked}
                onClick={() =>
                  void act(async () => {
                    const next = await templates.addQuestions(id, [...picked]);
                    setPicked(new Set());
                    return next;
                  })
                }
              >
                {t('templates.addSelected')}
              </Button>
            </div>
          </Card>
        </div>

        {/* ---------------------------------------------------------------- actions */}
        <Card className="nv-pad nv-stack">
          {/* Again here, beside the table: the one at the top is a screen away from the weight
              that caused it (field test B-41). */}
          {error ? <Alert>{error}</Alert> : null}
          {detail.problems.length > 0 ? (
            <Alert>
              <strong>{t('templates.cannotPublish')}</strong>
              <ul className="nv-reasons">
                {detail.problems.map((problem) => (
                  <li key={problem.code + JSON.stringify(problem.args)}>
                    {t(problem.code, problem.args)}
                  </li>
                ))}
              </ul>
            </Alert>
          ) : locked ? (
            <Alert tone="info">{t('templates.lockedNotice', { version: s.version })}</Alert>
          ) : (
            <Alert tone="info">{t('templates.readyToPublish')}</Alert>
          )}

          <div className="nv-actions-bar">
            {/* A newer version exists, so this one is the end of its line: revising it would
                ask for a number that is taken. The way on is to the newer one, not a button
                that the server can only refuse. */}
            {locked && detail.supersededBy ? (
              <Button
                variant="secondary"
                icon={IconArrowRight}
                onClick={() => navigate(`/sablonlar/${detail.supersededBy!.id}`)}
              >
                {t('templates.goToNewest', { version: detail.supersededBy.version })}
              </Button>
            ) : locked ? (
              <Button
                variant="primary"
                icon={IconPlus}
                onClick={() =>
                  void (async () => {
                    try {
                      const revision = await templates.revise(id);
                      navigate(`/sablonlar/${revision.summary.id}`);
                    } catch (e) {
                      setError(describeError(e, 'error.actionFailed'));
                    }
                  })()
                }
              >
                {t('templates.newVersion', { version: s.version + 1 })}
              </Button>
            ) : (
              <Button
                variant="primary"
                icon={IconCheck}
                disabled={detail.problems.length > 0}
                // Asked first: a published version cannot be changed, only replaced by the
                // next one, and the button said nothing of that (field test, 2 November).
                onClick={() => setPublishing(true)}
              >
                {t('templates.publish')}
              </Button>
            )}

            {/*
              * Not under points, where every category already is the sum of its questions
              * and goes on being it. The button existed because that had to be asked for;
              * the whole point of the model is that it no longer does.
              */}
            {!locked && !points && detail.categories.length > 0 ? (
              <Button variant="ghost" onClick={() => void act(() => templates.flattenCategoryWeights(id))}>
                {t('templates.flatten')}
              </Button>
            ) : null}

            {s.status === 'Published' ? (
              <Button variant="ghost" icon={IconArchive} onClick={() => void act(() => templates.archive(id))}>
                {t('templates.archive')}
              </Button>
            ) : null}

            {/*
              * Under points the total is a figure with a right answer, so it is a chip that
              * goes amber when it is wrong rather than a line of grey text at the end of a
              * row — this is the number the designer is working towards, and the one that
              * will stop them publishing.
              */}
            {points ? (
              <span className="nv-actions-bar__end">
                <Badge tone={offTarget ? 'na' : 'comment'} shape="square">
                  {t('templates.totalPoints', { total: totalWeight, target: s.pointsTarget ?? 0 })}
                </Badge>
              </span>
            ) : (
              <span className="nv-hint nv-actions-bar__end">
                {t('templates.totalWeight', { weight: totalWeight })}
              </span>
            )}
          </div>

          {!locked && !points && detail.categories.length > 0 ? (
            <p className="nv-hint">{t('templates.flattenHint')}</p>
          ) : null}

          {!locked && points ? (
            <p className="nv-hint">{t('templates.pointsHint')}</p>
          ) : null}
        </Card>
      </div>

      <ConfirmDialog
        open={publishing}
        tone="primary"
        title={t('templates.publishTitle', { name: s.name, version: String(s.version) })}
        confirmLabel={t('templates.publish')}
        onConfirm={async () => {
          await act(() => templates.publish(id));
        }}
        onClose={() => setPublishing(false)}
      >
        {t('templates.publishBody')}
      </ConfirmDialog>

      <ConfirmDialog
        open={doomedCategory !== null}
        title={doomedCategory ? t('templates.removeCategoryTitle', { name: doomedCategory.name }) : ''}
        confirmLabel={t('common.delete')}
        onConfirm={async () => {
          if (!doomedCategory) return;

          await act(() => templates.removeCategory(id, doomedCategory.id));
          setDoomedCategory(null);
        }}
        onClose={() => setDoomedCategory(null)}
      >
        <p>{t('templates.removeCategoryWarning', { count: doomedCategory?.questions.length ?? 0 })}</p>
      </ConfirmDialog>
    </>
  );
}
